<script>
  import { mapActions } from 'vuex';
  import { useAlert } from 'dashboard/composables';

  export default{
    props:{
      isEditingService:{
        type: Boolean,
        default: false,
      },
      branchId:{
        type: String,
        default: null,
      },
      serviceId:{
        type: String,
        default: null,
      },
      resetCreateService:{
        type: Function,
        default: () => {},
      },
    },
    data(){
      return{
        serviceName: '',
        duration: 60,
        maxServe: 1,
        pricingDetails: '',
        description: '',
        imageFile: null,
        imagePreview: null,
      }
    },
    methods: {
    ...mapActions('appointments', ['createService', 'editService', 'fetchServices', 'fetchService']),
    
    handleImageUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        useAlert('Only JPG/PNG images are allowed.');
        event.target.value = '';
        return;
      }

      this.imageFile = file;

      // Create image preview
      const reader = new FileReader();
      reader.onload = (e) => {
        this.imagePreview = e.target.result;
      };
      reader.readAsDataURL(file);
    },
    
    clearImage() {
      this.imageFile = null;
      this.imagePreview = null;
      // Reset the file input
      this.$refs.imageInput.value = '';
    },

    async handleCreateService() {
      if (!this.serviceName) {
        useAlert(this.$t('BRANCHES.CREATE_SERVICE_MODAL.VALIDATION.FILL_FIELDS'));
        return;
      }
      try {
        const payload = {
          "service_name": this.serviceName,
          "duration": this.duration / 60,
          "max_serve": this.maxServe,
          "price": this.pricingDetails,
          "description": this.description,
        };
        
        // Only add image if it exists - send the actual File object
        if (this.imageFile) {
          payload.image = this.imageFile;
        }
        
        await this.createService({ branchId: this.branchId, payload });
      } catch (err) {
        console.error(err);
        useAlert(err); // TODO: i18n if applicable
      } finally {
        this.fetchServices(this.branchId);
        this.resetCreateService();
      }
    },

    async handleEditService() {
      if (!this.serviceName) {
        useAlert(this.$t('BRANCHES.CREATE_SERVICE_MODAL.VALIDATION.FILL_FIELDS'));
        return;
      }
      try {
        const payload = {
          "service_name": this.serviceName,
          "duration": this.duration / 60,
          "max_serve": this.maxServe,
          "price": this.pricingDetails,
          "description": this.description,
          // Set delete_image to true if there's no image file and no preview
          "delete_image": !this.imageFile && !this.imagePreview
        };
        
        // Only add image if it exists - send the actual File object
        if (this.imageFile) {
          payload.image = this.imageFile;
        }
        
        await this.editService({ serviceId: this.serviceId, payload });
      } catch (err) {
        console.error(err);
        useAlert(err); // TODO: i18n if applicable
      } finally {
        this.fetchServices(this.branchId);
        this.resetCreateService();
      }
    }
  },
  async mounted(){
    try{
      if(this.isEditingService){
        const response = await this.fetchService(this.serviceId);
        this.serviceName = response?.service_name;
        this.duration = Math.round(response?.duration * 60);
        this.maxServe = response?.max_serve;
        this.pricingDetails = response?.price;
        this.priceUnit = response?.price_unit || 'MYR';
        this.description = response?.description || '';
        
        // If service has an image, set the preview
        if (response?.image) {
          this.imagePreview = response.presigned_image_url;
        }
      }
    }catch(err){
      console.error(err);
      useAlert(err);
    }
  }
}
</script>

<template>
  <div class="p-6 border-2 rounded-lg bg-white dark:bg-slate-800 shadow-lg max-w-md w-full mx-auto">
    <!-- Service Name -->
    <label class="block mb-4">
      <span class="text-sm font-medium text-slate-700 dark:text-slate-300">{{ $t('BRANCHES.CREATE_SERVICE_MODAL.TITLE') }}</span>
      <input 
        type="text" 
        v-model="serviceName" 
        :placeholder="$t('BRANCHES.CREATE_SERVICE_MODAL.NAME_PLACEHOLDER')"
        class="mt-1 w-full p-3 border rounded-md text-slate-900 dark:text-white dark:bg-slate-700 dark:border-slate-600 focus:ring-2 focus:ring-woot-500 focus:outline-none"
      />
    </label>

    <!-- Duration -->
    <label class="block mb-4">
      <span class="text-sm font-medium text-slate-700 dark:text-slate-300">{{ $t('BRANCHES.CREATE_SERVICE_MODAL.DURATION_LABEL') }}</span>
      <div class="flex items-center gap-2 mt-1">
        <input 
          type="number" 
          v-model="duration" 
          min="1"
          class="w-20 p-3 border rounded-md text-slate-900 dark:text-white dark:bg-slate-700 dark:border-slate-600 focus:ring-2 focus:ring-woot-500 focus:outline-none"
        />
        <span class="text-slate-600 dark:text-slate-300">{{ duration > 1 ? $t('BRANCHES.CREATE_SERVICE_MODAL.TIME_UNITS.MINUTES') : $t('BRANCHES.CREATE_SERVICE_MODAL.TIME_UNITS.MINUTE') }}</span>
      </div>
    </label>

    <!-- Max Serve -->
    <label class="block mb-4">
      <span class="text-sm font-medium text-slate-700 dark:text-slate-300">{{ $t('BRANCHES.CREATE_SERVICE_MODAL.MAX_SERVE_LABEL') }}</span>
      <div class="flex items-center gap-2 mt-1">
        <input 
          type="number" 
          v-model="maxServe" 
          min="1"
          class="w-20 p-3 border rounded-md text-slate-900 dark:text-white dark:bg-slate-700 dark:border-slate-600 focus:ring-2 focus:ring-woot-500 focus:outline-none"
        />
        <span class="text-slate-600 dark:text-slate-300">{{ maxServe > 1 ? $t('BRANCHES.CREATE_SERVICE_MODAL.TIME_UNITS.CLIENTS') : $t('BRANCHES.CREATE_SERVICE_MODAL.TIME_UNITS.CLIENT') }}</span>
      </div>
    </label>

    <!-- Description -->
    <label class="block mb-4">
      <span class="text-sm font-medium text-slate-700 dark:text-slate-300">{{ $t('BRANCHES.CREATE_SERVICE_MODAL.DESCRIPTION_LABEL') }}</span>
      <textarea
        v-model="description"
        rows="3"
        :placeholder="$t('BRANCHES.CREATE_SERVICE_MODAL.DESCRIPTION_PLACEHOLDER')"
        class="mt-1 w-full p-3 border rounded-md text-slate-900 dark:text-white dark:bg-slate-700 dark:border-slate-600 focus:ring-2 focus:ring-woot-500 focus:outline-none"
      ></textarea>
    </label>
    
    <!-- Pricing Details -->
    <div class="block mb-4">
      <label class="block">
        <span class="text-sm font-medium text-slate-700 dark:text-slate-300">{{ $t('BRANCHES.CREATE_SERVICE_MODAL.PRICING_LABEL') }}</span>
        <textarea
          v-model="pricingDetails" 
          :placeholder="$t('BRANCHES.CREATE_SERVICE_MODAL.PRICING_PLACEHOLDER')"
          class="mt-1 w-full p-3 border rounded-md text-slate-900 dark:text-white dark:bg-slate-700 dark:border-slate-600 focus:ring-2 focus:ring-woot-500 focus:outline-none"
        >
        </textarea>
      </label>
    </div>
    
    <!-- Image Upload -->
    <label class="block mb-4">
      <span class="text-sm font-medium text-slate-700 dark:text-slate-300">{{ $t('BRANCHES.CREATE_SERVICE_MODAL.IMAGE_LABEL') }}</span>
      <div class="mt-1 flex items-center">
        <input
          ref="imageInput"
          type="file"
          accept="image/jpeg,image/jpg,image/png"
          @change="handleImageUpload"
          class="hidden"
        />
        
        <!-- Image Preview -->
        <div v-if="imagePreview" class="relative w-24 h-24 mr-4 overflow-hidden rounded-md">
          <img 
            :src="imagePreview" 
            class="w-full h-full object-cover"
            :alt="$t('BRANCHES.CREATE_SERVICE_MODAL.IMAGE_PREVIEW_ALT')"
          />
          <button
            @click="clearImage"
            class="absolute top-1 right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
            :title="$t('BRANCHES.CREATE_SERVICE_MODAL.REMOVE_IMAGE')"
          >
            ×
          </button>
        </div>
        
        <!-- Upload Button -->
        <button
          type="button"
          @click="$refs.imageInput.click()"
          class="px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-md text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-woot-500"
        >
          {{ imagePreview ? $t('BRANCHES.CREATE_SERVICE_MODAL.CHANGE_IMAGE') : $t('BRANCHES.CREATE_SERVICE_MODAL.UPLOAD_IMAGE') }}
        </button>
      </div>
      <p class="mt-1 text-xs text-slate-500 dark:text-slate-400">
        {{ $t('BRANCHES.CREATE_SERVICE_MODAL.IMAGE_RECOMMENDATION') }} · JPG/PNG only
      </p>
    </label>

    <!-- Action Buttons -->
    <div class="flex gap-2 mt-4">
      <button 
        @click="isEditingService ? handleEditService() : handleCreateService()"
        class="w-full py-2 text-white font-medium rounded-md transition bg-woot-600 hover:bg-woot-700 focus:ring-2 focus:ring-woot-400 focus:outline-none"
      >
        {{ isEditingService ? $t('BRANCHES.CREATE_SERVICE_MODAL.BUTTONS.EDIT') : $t('BRANCHES.CREATE_SERVICE_MODAL.BUTTONS.CREATE') }}
      </button>
      <button 
        @click="resetCreateService"
        class="w-full py-2 text-slate-700 dark:text-slate-300 font-medium rounded-md border border-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 transition"
      >
        {{ $t('BRANCHES.CREATE_SERVICE_MODAL.BUTTONS.CANCEL') }}
      </button>
    </div>
  </div>
</template>
